import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { loadUserPerformanceHistory, getTodayKey } from '../services/gameStateService';

interface PerformanceData {
  errors: number;
  completedAt: string;
  circle: number;
  date: string;
}

interface PerformanceHistogramProps {
  className?: string;
}

const PerformanceHistogram: React.FC<PerformanceHistogramProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const [performanceHistory, setPerformanceHistory] = useState<Record<string, PerformanceData> | null>(null);
  const [loading, setLoading] = useState(true);

  const circleEmojis = ['🟣', '🔵', '🟢', '🟡', '🔴'];
  const circleLabels = ['1st', '2nd', '3rd', '4th', '5th'];
  const circleColors = {
    1: '#8B5CF6', // purple
    2: '#3B82F6', // blue  
    3: '#10B981', // green
    4: '#F59E0B', // yellow
    5: '#EF4444', // red
  };

  useEffect(() => {
    const loadHistory = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const history = await loadUserPerformanceHistory(user);
        setPerformanceHistory(history);
      } catch (error) {
        console.error('Error loading performance history:', error);
        setPerformanceHistory(null);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [user]);

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5].map((circle) => (
            <div key={circle} className="flex items-center gap-4">
              <div className="w-8 h-4 bg-gray-200 rounded"></div>
              <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
              <div className="flex-1 h-4 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Anonymous users can still view their performance history

  // Calculate performance counts for each circle
  const circleCounts = [0, 0, 0, 0, 0]; // Index 0 = 1st circle, etc.
  const todayKey = getTodayKey();
  let todayCircle: number | null = null;

  if (performanceHistory) {
    Object.entries(performanceHistory).forEach(([date, data]) => {
      const circle = data.circle;
      if (circle >= 1 && circle <= 5) {
        circleCounts[circle - 1]++;
        if (date === todayKey) {
          todayCircle = circle;
        }
      }
    });
  }

  const maxCount = Math.max(...circleCounts, 1); // Ensure at least 1 for scaling

  return (
    <div className={className}>
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((circle, index) => {
          const count = circleCounts[index];
          const isToday = todayCircle === circle;
          const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;

          return (
            <motion.div
              key={circle}
              className="flex items-center gap-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Circle Label */}
              <div className="w-8 text-sm font-semibold text-gray-700 text-right">
                {circleLabels[index]}
              </div>

              {/* Circle Emoji */}
              <div className="text-lg">
                {circleEmojis[index]}
              </div>

              {/* Histogram Bar Container */}
              <div className="flex-1 relative">
                <div className="h-6 bg-gray-100 rounded-full relative overflow-hidden">
                  {/* Background bar */}
                  <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                  
                  {/* Performance bar */}
                  <motion.div
                    className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: isToday 
                        ? circleColors[circle as keyof typeof circleColors]
                        : '#6B7280' // dark gray
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${barWidth}%` }}
                    transition={{ delay: index * 0.1 + 0.3, duration: 0.6, ease: "easeOut" }}
                  />
                  
                  {/* Count label */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xs font-bold ${
                      count > 0 
                        ? isToday 
                          ? 'text-white' 
                          : barWidth > 30 
                            ? 'text-white' 
                            : 'text-gray-700'
                        : 'text-gray-500'
                    }`}>
                      {count > 0 ? count : ''}
                    </span>
                  </div>
                </div>
                
                {/* Today indicator */}
                {isToday && (
                  <motion.div
                    className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
                    style={{ backgroundColor: circleColors[circle as keyof typeof circleColors] }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.6, type: "spring", stiffness: 300 }}
                  >
                    <div className="absolute inset-0 rounded-full animate-ping" 
                         style={{ 
                           backgroundColor: circleColors[circle as keyof typeof circleColors],
                           opacity: 0.75
                         }}>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      {performanceHistory && Object.keys(performanceHistory).length > 0 && (
        <motion.div
          className="mt-4 text-xs text-gray-500 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Total games: {Object.keys(performanceHistory).length}
          {todayCircle && (
            <span className="ml-4 font-semibold" 
                  style={{ color: circleColors[todayCircle as keyof typeof circleColors] }}>
              Today: {circleLabels[todayCircle - 1]} Circle
            </span>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default PerformanceHistogram;